<div class="flex flex-col gap-4 p-4">
  <!-- Header -->
  <div class="flex items-center justify-between">
    <div>
      <h1 class="text-default text-xl font-bold">Support Dashboard</h1>
      <p class="text-secondary text-sm">Happiness Center tickets, SLA compliance and categorisation</p>
    </div>
  </div>

  <!-- Filter Bar -->
  <div class="rounded-xl border bg-white px-4 py-1">
    <app-global-filter-panel [config]="filterConfig" (filtersChanged)="onFiltersChanged($event)" />
  </div>

  <!-- KPI Row -->
  <app-kpi-card-grid [cards]="ticketKpis" title="ONLINE TICKET OVERVIEW" [columns]="5" />

  <!-- Row: Requests by Type/Community + Open Tickets -->
  <div class="grid grid-cols-1 gap-4 lg:grid-cols-2">
    <!-- Requests by Type -->
    <div class="rounded-xl border border-gray-100 bg-white p-4 shadow-sm">
      <div class="mb-3 flex items-center justify-between gap-2">
        <div class="flex items-center gap-2">
          <div class="flex h-7 w-7 items-center justify-center rounded-lg bg-purple-50">
            <mat-icon class="!text-sm text-purple-500">category</mat-icon>
          </div>
          <h3 class="text-default text-sm font-bold">Open Requests by Type &amp; Community</h3>
        </div>
      </div>
      @if (!hasTicketPermission) {
        <div class="flex items-center justify-center gap-2 rounded-lg border border-red-200 bg-red-50 p-6 text-sm font-semibold text-red-700">
          <mat-icon class="!text-base text-red-400">lock</mat-icon>
          Permissions Restricted
        </div>
      } @else if (loadingCategory) {
        <div class="flex justify-center py-6"><mat-spinner diameter="28" /></div>
      } @else if (typeItems.length > 0 || communityItems.length > 0) {
        @if (typeItems.length > 0) {
          <div class="mb-2 flex items-center gap-2">
            <span class="text-[11px] font-bold uppercase tracking-wider text-gray-400">By Type</span>
            <div class="flex-1 border-t border-gray-100"></div>
          </div>
          <app-labeled-count-list [items]="typeItems" />
        }

        @if (communityItems.length > 0) {
          <div class="mb-2 mt-3 flex items-center gap-2">
            <span class="text-[11px] font-bold uppercase tracking-wider text-gray-400">By Community</span>
            <div class="flex-1 border-t border-gray-100"></div>
          </div>
          <app-labeled-count-list [items]="communityItems" />
        }
      } @else if (!loadingCategory) {
        <div class="flex flex-col items-center gap-1.5 rounded-lg border border-dashed border-gray-200 py-8 text-center">
          <mat-icon class="!text-3xl text-gray-300">category</mat-icon>
          <span class="text-xs text-gray-400">No request data available</span>
        </div>
      }
    </div>

    <!-- Open Tickets -->
    <div class="rounded-xl border border-gray-100 bg-white p-4 shadow-sm">
      <div class="mb-3 flex items-center justify-between gap-2">
        <div class="flex items-center gap-2">
          <div class="flex h-7 w-7 items-center justify-center rounded-lg bg-indigo-50">
            <mat-icon class="!text-sm text-indigo-500">support_agent</mat-icon>
          </div>
          <div>
            <h3 class="text-default text-sm font-bold">Open Tickets</h3>
            <p class="text-secondary text-xs">Oldest unresolved requests, sorted by age</p>
          </div>
        </div>
        @if (openData?.total > 0) {
          <span class="rounded-full px-2 py-0.5 text-[10px] font-bold" [ngClass]="openData.total | countBadge">{{ openData.total }}</span>
        }
      </div>
      @if (!hasTicketPermission) {
        <div class="flex items-center justify-center gap-2 rounded-lg border border-red-200 bg-red-50 p-6 text-sm font-semibold text-red-700">
          <mat-icon class="!text-base text-red-400">lock</mat-icon>
          Permissions Restricted
        </div>
      } @else if (loadingOpen) {
        <div class="flex justify-center py-6"><mat-spinner diameter="28" /></div>
      } @else if (openData?.tickets?.length > 0) {
        <div class="flex flex-col gap-1">
          @for (t of openData.tickets; track t.id) {
            <a
              [routerLink]="['/happiness-center/online']"
              [queryParams]="ticketQueryParams(t)"
              class="flex items-center gap-2.5 overflow-hidden rounded-lg border border-l-4 border-gray-100 bg-white py-2 pl-2.5 pr-2.5 shadow-sm transition-colors hover:bg-gray-50"
              [class.border-l-red-500]="t.age_days > 7"
              [class.border-l-orange-400]="t.age_days > 3 && t.age_days <= 7"
              [class.border-l-blue-300]="t.age_days <= 3"
            >
              <!-- Icon -->
              <div
                class="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-md"
                [class.bg-red-50]="t.age_days > 7"
                [class.bg-orange-50]="t.age_days > 3 && t.age_days <= 7"
                [class.bg-blue-50]="t.age_days <= 3"
              >
                <mat-icon class="!text-sm !leading-none" [class.text-red-500]="t.age_days > 7" [class.text-orange-500]="t.age_days > 3 && t.age_days <= 7" [class.text-blue-400]="t.age_days <= 3"
                  >confirmation_number</mat-icon
                >
              </div>
              <!-- Content -->
              <div class="min-w-0 flex-1">
                <div class="flex items-baseline gap-1.5">
                  <p class="line-clamp-1 text-xs font-semibold uppercase leading-snug text-gray-800">{{ t.type || '—' }}</p>
                  @if (t.reference) {
                    <span class="flex-shrink-0 font-mono text-[10px] text-gray-400">{{ t.reference }}</span>
                  }
                </div>
                <div class="mt-0.5 flex flex-wrap items-center gap-x-1 text-[10px] text-gray-400">
                  @if (t.community_name) {
                    <span>{{ t.community_name }}</span>
                    <span class="text-gray-300">·</span>
                  }
                  <span>{{ t.assigned_to || 'Unassigned' }}</span>
                </div>
              </div>
              <!-- Status + age -->
              <div class="flex flex-shrink-0 flex-col items-end gap-1">
                <span class="rounded-full px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider" [ngClass]="getStatusBadgeClass(t.status)">{{ t.status }}</span>
                <span
                  class="rounded-full px-1.5 py-0.5 text-[9px] font-bold"
                  [class.bg-red-100]="t.age_days > 7"
                  [class.text-red-700]="t.age_days > 7"
                  [class.bg-orange-100]="t.age_days > 3 && t.age_days <= 7"
                  [class.text-orange-700]="t.age_days > 3 && t.age_days <= 7"
                  [class.bg-gray-100]="t.age_days <= 3"
                  [class.text-gray-500]="t.age_days <= 3"
                  >{{ t.age_days }} d</span
                >
              </div>
            </a>
          }
        </div>
      } @else if (!loadingOpen) {
        <div class="flex flex-col items-center gap-1.5 rounded-lg border border-dashed border-gray-200 py-8 text-center">
          <mat-icon class="!text-3xl text-gray-300">inbox</mat-icon>
          <span class="text-xs text-gray-400">No open tickets</span>
        </div>
      }
    </div>
  </div>

  @if (hasMollakPermission) {
    <!-- Mollak KPI Row -->
    <app-kpi-card-grid [cards]="mollakKpis" title="MOLLAK SERVICE REQUESTS" [columns]="5" />

    <!-- Row: Mollak Requests by Type/Community + Open Mollak Requests -->
    <div class="grid grid-cols-1 gap-4 lg:grid-cols-2">
      <!-- Mollak Requests by Type -->
      <div class="rounded-xl border border-gray-100 bg-white p-4 shadow-sm">
        <div class="mb-3 flex items-center justify-between gap-2">
          <div class="flex items-center gap-2">
            <div class="flex h-7 w-7 items-center justify-center rounded-lg bg-purple-50">
              <mat-icon class="!text-sm text-purple-500">category</mat-icon>
            </div>
            <h3 class="text-default text-sm font-bold">Open Mollak Requests by Type &amp; Community</h3>
          </div>
        </div>
        @if (loadingMollak) {
          <div class="flex justify-center py-6"><mat-spinner diameter="28" /></div>
        } @else if (mollakTypeItems.length > 0 || mollakCommunityItems.length > 0) {
          @if (mollakTypeItems.length > 0) {
            <div class="mb-2 flex items-center gap-2">
              <span class="text-[11px] font-bold uppercase tracking-wider text-gray-400">By Type</span>
              <div class="flex-1 border-t border-gray-100"></div>
            </div>
            <app-labeled-count-list [items]="mollakTypeItems" />
          }

          @if (mollakCommunityItems.length > 0) {
            <div class="mb-2 mt-3 flex items-center gap-2">
              <span class="text-[11px] font-bold uppercase tracking-wider text-gray-400">By Community</span>
              <div class="flex-1 border-t border-gray-100"></div>
            </div>
            <app-labeled-count-list [items]="mollakCommunityItems" />
          }
        } @else if (!loadingMollak) {
          <div class="flex flex-col items-center gap-1.5 rounded-lg border border-dashed border-gray-200 py-8 text-center">
            <mat-icon class="!text-3xl text-gray-300">category</mat-icon>
            <span class="text-xs text-gray-400">No request data available</span>
          </div>
        }
      </div>

      <!-- Open Mollak Requests -->
      <div class="rounded-xl border border-gray-100 bg-white p-4 shadow-sm">
        <div class="mb-3 flex items-center justify-between gap-2">
          <div class="flex items-center gap-2">
            <div class="flex h-7 w-7 items-center justify-center rounded-lg bg-indigo-50">
              <mat-icon class="!text-sm text-indigo-500">assignment_late</mat-icon>
            </div>
            <div>
              <h3 class="text-default text-sm font-bold">Open Mollak Requests</h3>
              <p class="text-secondary text-xs">Closest to the KPI due date first</p>
            </div>
          </div>
          <div class="flex items-center gap-2">
            @if (mollakOpenData?.total > 0) {
              <span class="rounded-full px-2 py-0.5 text-[10px] font-bold" [ngClass]="mollakOpenData.total | countBadge">{{ mollakOpenData.total }}</span>
            }
            <a routerLink="/happiness-center/mollak-service-requests" class="text-xs font-medium text-blue-600 hover:underline">View all →</a>
          </div>
        </div>
        @if (loadingMollakOpen) {
          <div class="flex justify-center py-6"><mat-spinner diameter="28" /></div>
        } @else if (mollakOpenData?.requests?.length > 0) {
          <div class="flex flex-col gap-1">
            @for (r of mollakOpenData.requests; track r.id) {
              <a
                (click)="openMollakRequest(r.id)"
                class="flex cursor-pointer items-center gap-2.5 overflow-hidden rounded-lg border border-l-4 border-gray-100 bg-white py-2 pl-2.5 pr-2.5 shadow-sm transition-colors hover:bg-gray-50"
                [class.border-l-red-500]="r.days_overdue !== null"
                [class.border-l-blue-300]="r.days_overdue === null"
              >
                <!-- Icon -->
                <div class="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-md" [class.bg-red-50]="r.days_overdue !== null" [class.bg-blue-50]="r.days_overdue === null">
                  <mat-icon class="!text-sm !leading-none" [class.text-red-500]="r.days_overdue !== null" [class.text-blue-400]="r.days_overdue === null">assignment</mat-icon>
                </div>
                <!-- Content -->
                <div class="min-w-0 flex-1">
                  <div class="flex items-baseline gap-1.5">
                    <p class="line-clamp-1 text-xs font-semibold uppercase leading-snug text-gray-800">{{ r.request_type || '—' }}</p>
                    <span class="flex-shrink-0 font-mono text-[10px] text-gray-400">{{ r.transaction_number }}</span>
                  </div>
                  <div class="mt-0.5 flex flex-wrap items-center gap-x-1 text-[10px] text-gray-400">
                    @if (r.community_name) {
                      <span>{{ r.community_name }}</span>
                      @if (r.unit_number) {
                        <span class="text-gray-300">·</span>
                        <span>{{ r.unit_number }}</span>
                      }
                    } @else {
                      <span>Not linked</span>
                    }
                  </div>
                </div>
                <!-- Status + KPI due date -->
                <div class="flex flex-shrink-0 flex-col items-end gap-1">
                  <span class="rounded-full px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider" [ngClass]="getMollakStatusBadgeClass(r.status)">{{ r.status }}</span>
                  @if (r.kpi_due_at) {
                    <span
                      class="rounded-full px-1.5 py-0.5 text-[9px] font-bold"
                      [class.bg-red-100]="r.days_overdue !== null"
                      [class.text-red-700]="r.days_overdue !== null"
                      [class.bg-gray-100]="r.days_overdue === null"
                      [class.text-gray-500]="r.days_overdue === null"
                      >{{ r.kpi_due_at | date }}{{ r.days_overdue !== null ? ' · ' + r.days_overdue + ' d overdue' : '' }}</span
                    >
                  }
                </div>
              </a>
            }
          </div>
        } @else if (!loadingMollakOpen) {
          <div class="flex flex-col items-center gap-1.5 rounded-lg border border-dashed border-gray-200 py-8 text-center">
            <mat-icon class="!text-3xl text-gray-300">inbox</mat-icon>
            <span class="text-xs text-gray-400">No open Mollak requests</span>
          </div>
        }
      </div>
    </div>
  }

  <!-- Bottom Row: SLA Compliance + Surveys + Timeline -->
  <div class="grid grid-cols-1 gap-4 lg:grid-cols-3">
    <!-- SLA Compliance -->
    <div class="rounded-xl border border-gray-100 bg-white p-4 shadow-sm">
      <h3
        class="text-secondary mb-3 text-sm font-semibold uppercase tracking-wide"
        matTooltip="Percentage of tickets created in the selected date range that were resolved within 72 hours of creation. Tickets still open are checked against the same 72-hour window using the current time in place of a resolution date."
      >
        SLA Compliance
      </h3>
      @if (!hasTicketPermission) {
        <div class="flex items-center justify-center gap-2 rounded-lg border border-red-200 bg-red-50 p-6 text-sm font-semibold text-red-700">
          <mat-icon class="!text-base text-red-400">lock</mat-icon>
          Permissions Restricted
        </div>
      } @else if (loadingSla) {
        <div class="flex justify-center py-6"><mat-spinner diameter="28" /></div>
      } @else if (slaData) {
        <div
          class="rounded-lg p-4 text-center"
          [ngClass]="slaData.overall_pct >= 90 ? 'border border-green-100 bg-green-50' : slaData.overall_pct >= 70 ? 'border border-yellow-100 bg-yellow-50' : 'border border-red-100 bg-red-50'"
        >
          <div class="text-3xl font-bold" [ngClass]="slaData.overall_pct >= 90 ? 'text-green-700' : slaData.overall_pct >= 70 ? 'text-yellow-700' : 'text-red-700'">
            {{ slaData.overall_pct ?? 0 }}%
          </div>
          <div class="text-secondary mt-1 text-[10px] font-semibold uppercase tracking-wide">Within {{ slaData.sla_hours }}h SLA</div>
          <div class="mt-2 h-2 w-full overflow-hidden rounded-full bg-gray-100">
            <div class="h-full rounded-full transition-all" [style.width.%]="slaData.overall_pct" [ngClass]="getSlaBarColor(slaData.overall_pct)"></div>
          </div>
          @if (slaData.breached_total > 0) {
            <div class="mt-1.5 text-xs text-red-600">{{ slaData.breached_total }} of {{ slaData.total_tickets }} breached</div>
          }
        </div>
      }
    </div>

    <div class="rounded-xl border border-gray-100 bg-white p-4 shadow-sm">
      <app-survey-overview [dashboardType]="dashboardType" [filters]="filters" />
    </div>
    <div class="rounded-xl border border-gray-100 bg-white p-4 shadow-sm">
      <app-activity-timeline [requiredPermission]="'activity_logs_other'" />
    </div>
  </div>
</div>
